import React, { FC, useEffect, useState } from "react";
import { FaClock, FaCalculator, FaGlobe } from "react-icons/fa6";
import { FaCog } from "react-icons/fa";
import MyWorkTab from "./myWork/MyWorkTab";
import TeamWorkTab from "./teamWork/TeamWorkTab";
import PendingTeamWorkTab from "./unassignedTeamWork/UnAssignedTeamWork";
import { hasPermission } from "../../helpers/utils/AccessControlUtils";
import { useFindOrderCountByStatusesQuery } from "./homeApi";
import { AutoCompletionStats } from "../../components/common/AutoCompletionStats";
import { OrderCountHistogramSearchForm } from "../../components/common/graphs/histogram/OrderCountHistogramSearchForm";
import { AutomatedOrderHistogramSearchForm } from "../../components/common/graphs/histogram/AutomatedOrderHistogramSearchForm";
import DashboardCardView from "../../components/common/DashboardCardView";
import { useUtilDataQuery } from "../profile/profileApi";
import ResetPassword from "../../components/common/resetPassword/ResetPassword";
import PasswordExpireConfirmation from "../../components/common/resetPassword/PasswordExpireConfirmation";

const Home: FC = () => {
    const cardData = useFindOrderCountByStatusesQuery("");
    const [tabs, setTabs] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState("");
    const [loadedTabs, setLoadedTabs] = useState<string[]>([]);
    const [showPasswordExpiryModal, setShowPasswordExpiryModal] = useState(false);
    const [showRestModal, setShowRestModal] = useState(false);
    const isUserHaveTeam = JSON.parse(localStorage.getItem("isUserHaveTeam") || "false");
    const isUserManager = JSON.parse(localStorage.getItem("isUserManager") || "false");
    const { data: utilData } = useUtilDataQuery("");

    useEffect(() => {
        document.title = "Home";
        const daysToExpirePassword = localStorage.getItem("daysToExpirePassword");
        const days = daysToExpirePassword ? parseInt(JSON.parse(daysToExpirePassword)) : 0;
        setShowPasswordExpiryModal(days <= 14);
    }, []);

    useEffect(() => {
        const newTabs: any[] = [];

        if (isUserHaveTeam) {
            newTabs.push({
                id: "my-work",
                label: "My Work",
                Component: MyWorkTab,
            });
        }

        if (
            (utilData?.customerLevel !== "CUSTOMER" && isUserManager) ||
            (isUserManager && utilData?.customerLevel === "CUSTOMER" && utilData?.fulfillmentCandidate)
        ) {
            newTabs.push({
                id: "team-work",
                label: "Team Members Work",
                Component: TeamWorkTab,
            });

            newTabs.push({
                id: "unassigned-team-work",
                label: "Unassigned Team Work",
                Component: PendingTeamWorkTab,
            });
        }
        setTabs(newTabs);
    }, [utilData]);

    useEffect(() => {
        if (tabs.length === 0) return;

        const setTabFromHash = () => {
            const hash = window.location.hash.replace("#", "");
            const fallbackTab = tabs[0]?.id || "";
            const validTab = tabs.find(tab => tab.id === hash)?.id || fallbackTab;

            setActiveTab(validTab);
            document.title = tabs.find(tab => tab.id === validTab)?.label || "Home";

            // If URL did not have a hash, add one
            if (!hash && fallbackTab) {
                window.history.replaceState(null, "", `#${fallbackTab}`);
            }
        };

        setTabFromHash();
        window.addEventListener("hashchange", setTabFromHash);
        return () => window.removeEventListener("hashchange", setTabFromHash);
    }, [tabs]);

    useEffect(() => {
        if (activeTab && !loadedTabs.includes(activeTab)) {
            setLoadedTabs(prev => [...prev, activeTab]);
        }
    }, [activeTab]);

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        window.history.replaceState(null, "", `#${tabId}`);
        const label = tabs.find(t => t.id === tabId)?.label;
        if (label) document.title = label;
    };

    return (
        <>

            {
                hasPermission(["ORSTAT-V"]) && (
                    <>
                        <div className="grid lg:grid-cols-5 sm:grid-cols-1 md:grid-col-2 gap-5 mt-8 ms-4 me-2 bg-[#FFFFF]">
                            <DashboardCardView
                                label="Total Active Orders"
                                icon={FaCog}
                                color="bg-[#3EBFFF]"
                                data={cardData?.data?.activeOrderCount}
                                filterQuery={{
                                    path: "/orders",
                                    query: {
                                        orderStatus: [
                                            "NEW",
                                            "COMPLETED",
                                            "PENDING",
                                            "AWAITING_LOCATE",
                                            "ISSUE",
                                            "AWAITING_PARTNER",
                                            "AWAITING_FULFILLMENT"
                                        ]
                                    }
                                }}
                                isAllowedClick={hasPermission(["ORDR-R", "ORDR-V"], true)}
                                isLoading={cardData?.isLoading}
                            />

                            <DashboardCardView
                                label="Total Pending Tax"
                                icon={FaCalculator}
                                color="bg-highlight-5"
                                data={cardData?.data?.totalTGXPendingOrderCount}
                                filterQuery={{
                                    path: "/orders",
                                    query: {
                                        type: [
                                            "TAX_REPORT",
                                            "TAX_CERTIFICATE",
                                            "BUILDER_TAX_CERTIFICATE",
                                            "COMMERCIAL_TAX_CERTIFICATE"
                                        ],
                                        productStatuses: ["PENDING", "SAVED", "COMPLETED", "QC"]
                                    }
                                }}
                                isAllowedClick={hasPermission(["ORDR-R", "ORDR-V"], true)}
                                isLoading={cardData?.isLoading}
                            />

                            <DashboardCardView
                                label="Total Pending HOA"
                                icon={FaCalculator}
                                color="bg-[#088001]"
                                data={cardData?.data?.totalHOAPendingOrderCount}
                                filterQuery={{
                                    path: "/orders",
                                    query: {
                                        type: [
                                            "HOA_CONTACT",
                                            "HOA_FULL",
                                            "COMMERCIAL_HOA_FULL",
                                            "BUILDER_HOA_FULL",
                                            "COMMERCIAL_HOA_CONTACT",
                                            "BUILDER_HOA_CONTACT"
                                        ],
                                        productStatuses: ["PENDING", "SAVED", "COMPLETED", "QC"]
                                    }
                                }}
                                isAllowedClick={hasPermission(["ORDR-R", "ORDR-V"], true)}
                                isLoading={cardData?.isLoading}
                            />

                            <DashboardCardView
                                label="Total Pending National"
                                icon={FaGlobe}
                                color="bg-highlight-2"
                                data={cardData?.data?.totalNationalPendingOrderCount}
                                filterQuery={{
                                    path: "/orders",
                                    query: {
                                        active: "true",
                                        excludedStates: ["TX"],
                                        orderStatus: [
                                            "NEW",
                                            "PENDING",
                                            "AWAITING_LOCATE",
                                            "AWAITING_PARTNER",
                                            "AWAITING_FULFILLMENT",
                                            "COMPLETED",
                                            "ISSUE"
                                        ]
                                    }
                                }}
                                isAllowedClick={hasPermission(["ORDR-R", "ORDR-V"], true)}
                                isLoading={cardData?.isLoading}
                            />

                            <DashboardCardView
                                label="Total Rush Orders"
                                icon={FaClock}
                                color="bg-error-1"
                                data={cardData?.data?.isRushOrderCount}
                                filterQuery={{
                                    path: "/orders",
                                    query: {
                                        rush: "true",
                                        orderStatus: [
                                            "NEW",
                                            "PENDING",
                                            "AWAITING_LOCATE",
                                            "AWAITING_PARTNER",
                                            "AWAITING_FULFILLMENT",
                                            "COMPLETED"
                                        ]
                                    }
                                }}
                                isAllowedClick={hasPermission(["ORDR-R", "ORDR-V"], true)}
                                isLoading={cardData?.isLoading}
                            />
                        </div>
                    </>
                )
            }



            {hasPermission(["AUTOCOMPLETE-V"]) && <AutoCompletionStats />}
            {tabs.length > 0 && (
                <div className="px-4 py-2 pt-4 bootstrap-tabs-component">
                    <ul className="nav nav-tabs">
                        {tabs.map(tab => (
                            <li key={tab.id} className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                                    onClick={() => handleTabChange(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="tab-content ">
                        {tabs.length > 0 && loadedTabs.includes(activeTab) && (
                            <div className="active">
                                {React.createElement(tabs.find(tab => tab.id === activeTab)!.Component)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {hasPermission(["CHART-V"]) && (
                <>
                    <div className="shadow-[0px_0px_5px_rgba(0,0,0,0.2)] m-5 p-5 rounded">
                        <OrderCountHistogramSearchForm />
                    </div>
                    <div className="shadow-[0px_0px_5px_rgba(0,0,0,0.2)] m-5 p-5 rounded">
                        <AutomatedOrderHistogramSearchForm />
                    </div>
                </>
            )}

            {showPasswordExpiryModal &&
                <PasswordExpireConfirmation show={showPasswordExpiryModal}
                                            setShow={setShowPasswordExpiryModal}
                                            setShowRestModal={setShowRestModal}/>
            }
            {showRestModal && <ResetPassword page={'Profile'}
                                             show={showRestModal}
                                             setShow={setShowRestModal}/>}
        </>
    );
};

export default Home;
